/**
 * BOWL COMMITTEES, the client half. PURE: no React, no fetch.
 *
 * The incoming work order proposes John as the default owner and desk leaders
 * as secondary candidates. Proposals grant no membership or access. Only a
 * separately confirmed current server assignment supplies effective seats.
 * This file renders GET /api/bowl-committees and builds guarded links.
 */

export const FILTERS = [
  { key: 'soon', label: 'Next 8 weeks' },
  { key: 'all', label: 'All bowls' },
  { key: 'mine', label: 'Mine' },
  { key: 'open', label: 'Second seat open' },
  { key: 'gaps', label: 'Needs a fix' },
]

const lc = (s) => String(s || '').trim().toLowerCase()

/** Is this person on this bowl (owner, second, or the second's pair partner)?
 *  `names` may carry display names, emails AND login ids: the owner seat also
 *  matches by login (John Haggerty = `haggerty`), so a name edit cannot orphan it. */
export function onBowl(bowl, names = []) {
  const want = new Set(names.map(lc).filter(Boolean))
  return (bowl?.assignment?.people || []).some((p) => want.has(lc(p)))
    || (bowl?.assignment?.logins || []).some((p) => want.has(lc(p)))
}

/** The current server contract permits confirming John or clearing to proposal.
 * Desk leaders remain candidates for the separate second seat, not owner overrides. */
export const OWNER_RULING_NAME = 'John Haggerty'
export function ownerChoices() {
  return [{ value: OWNER_RULING_NAME, label: `${OWNER_RULING_NAME} (proposed default assignee)` }]
}

export function applyFilter(bowls = [], key = 'soon', names = []) {
  switch (key) {
    case 'soon': return bowls.filter((b) => b?.priority?.tier === 'P1')
    case 'mine': return bowls.filter((b) => onBowl(b, names))
    case 'open': return bowls.filter((b) => b?.assignment?.second?.source === 'open')
    case 'gaps': return bowls.filter((b) => (b?.gaps || []).length > 0)
    default: return bowls.slice()
  }
}

/** The second seat in words, never a bare blank. */
export function secondLabel(second) {
  if (!second || second.source === 'open' || !second.name) {
    return second?.tied?.length ? `Open: ${second.tied.join(' or ')}` : 'Open'
  }
  if (second.source === 'exec' && second.pairedWith && second.supportConfirmed !== true) return `${second.name} (support ${second.pairedWith} not confirmed)`
  return second.pairedWith ? `${second.name} + ${second.pairedWith}` : second.name
}

/** Who decided the second seat, for the stamp beside it. */
export function secondSource(second) {
  if (!second) return ''
  if (second.source === 'exec') return `set by ${second.authorizedBy || 'an exec'}`
  if (second.source === 'proposal') return `proposed by rule ${second.rule} (display-only proposal)`
  return 'an exec picks'
}

export function daysLabel(priority) {
  const d = priority?.daysToGame
  if (d == null) return 'date not confirmed'
  if (d < 0) return 'played'
  if (d === 0) return 'today'
  return `${d} day${d === 1 ? '' : 's'} out`
}

const ROLE = { executive: 'Executive', operations: 'Operations', team_services: 'Team services', partnerships: 'Partnerships' }
export const roleLabel = (r) => ROLE[r] || r || ''

/** Where a record-backed CTA goes. The Command Center drawer carries Art and Submit for Win. */
export function recordHref(championshipId) {
  return championshipId ? `/command-center?record=${encodeURIComponent(championshipId)}` : null
}

/** The draft payload for POST /api/outreach/draft-to-gmail. Draft only. */
export function draftPayload(bowl) {
  const d = bowl?.draft
  const id = bowl?.record?.championshipId
  if (!d || !id) return null
  return { championshipId: id, to: d.to, subject: d.subject, body: d.body, angle: 'bowl-committee' }
}

/** Counts that reconcile: every bowl is in exactly one priority tier. */
export function tierCounts(bowls = []) {
  const out = { P1: 0, P2: 0, P3: 0, PAST: 0 }
  for (const b of bowls) out[b?.priority?.tier] = (out[b?.priority?.tier] || 0) + 1
  return out
}

/** Navigation only. Agreement of numeric record/account targets is not provider
 * verification; the existing composer performs its current access/fact review. */
export function committeeComposerHref(bowl) {
  const record = bowl?.record
  if (!/^\d+$/.test(String(record?.championshipId || '')) || !/^\d+$/.test(String(record?.companyId || ''))) return null
  if (String(bowl?.ctas?.email?.championshipId || '') !== String(record.championshipId)) return null
  return recordHref(record.championshipId)
}
